import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Input, Modal, Switch, Tooltip, Typography } from 'antd';

import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import Validations from './validations';

interface IProps {
  product: IProduct;
  handleChange?: any;
  enabledValidation?: boolean;
}

function SalesRecovery(props: IProps) {
  const { product, handleChange, enabledValidation } = props;
  const { user } = useUserStore(({ user }) => ({ user }));

  const [validationErrors, setValidationErrors] = useState<any>([]);
  const [showAlertUncheck, setShowAlertUncheck] = useState(false);

  const prevProductRef = useRef(product);
  const prevEnabledValidationRef = useRef(enabledValidation);
  const didInitRecoveryEmailRef = useRef(false);

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const handleToggleAlertVisibility = useCallback(() => {
    setShowAlertUncheck(prev => !prev);
  }, []);

  const handleUncheckConfirmed = useCallback(
    (currentProduct: IProduct) => {
      currentProduct.recovery_email = '';

      if (handleChange) {
        handleChange('recovery_email', '');
        handleChange('sales_recovery', false);
      }

      handleToggleAlertVisibility();
    },
    [handleChange, handleToggleAlertVisibility]
  );

  useEffect(() => {
    if (didInitRecoveryEmailRef.current) {
      return;
    }

    if (product.recovery_email) {
      didInitRecoveryEmailRef.current = true;
      return;
    }

    const defaultSender = user.business_name || user.full_name;
    if (!defaultSender || !handleChange) {
      return;
    }

    didInitRecoveryEmailRef.current = true;
    handleChange('recovery_email', defaultSender);
  }, [product.recovery_email, user.business_name, user.full_name, handleChange]);

  useEffect(() => {
    const prevProduct = prevProductRef.current;
    const prevEnabledValidation = prevEnabledValidationRef.current;
    const hasChangedProduct = product !== prevProduct;

    if (hasChangedProduct) {
      const isOpenValue = product.charge_type === EChargeType.OTHER;

      if (isOpenValue && product.sales_recovery) {
        if (handleChange) handleChange('sales_recovery', false);
      }
    }

    const hasEnabledValidation = !prevEnabledValidation && enabledValidation;
    const hasChangedProductWithValidation = enabledValidation && hasChangedProduct;
    if (hasEnabledValidation || hasChangedProductWithValidation) setValidationErrors({ ...run(product, Validations) });
    prevProductRef.current = product;
    prevEnabledValidationRef.current = enabledValidation;
  }, [product, enabledValidation, handleChange]);

  const isOpenValue = product.charge_type === EChargeType.OTHER;

  return (
    <FormContent title='Recuperação de vendas'>
      <div className='flex flex-col gap-4'>
        <div>
          <Tooltip title={isOpenValue ? '*Não é possível ativar a recuperação com Valor Aberto' : ''} placement='left'>
            <Typography.Text className='flex items-center gap-2' id='sales_recovery'>
              <Switch
                id='new-product-sales-recovery'
                disabled={isOpenValue}
                checked={product.sales_recovery}
                onChange={checked => {
                  if (checked) {
                    if (handleChange) handleChange('sales_recovery', checked);
                    return;
                  }

                  setShowAlertUncheck(true);
                }}
              />
              Recuperar vendas perdidas
            </Typography.Text>
          </Tooltip>
          <Typography.Text className='text-content-subtitle mt-1 mb-0 block'>
            A Eduzz tentará recuperar vendas não concretizadas.{' '}
            <Typography.Link
              id='new-product-sales-recovery-help'
              target='_blank'
              href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807625289-Como-funciona-a-recupera%C3%A7%C3%A3o-de-vendas-perdidas-RVP-'
            >
              Saiba Mais
            </Typography.Link>
          </Typography.Text>
        </div>

        {product.sales_recovery && (
          <Tooltip title='O nome definido neste campo aparecerá como remetente nos e-mails enviados de Recuperação de Vendas.'>
            <div className='relative z-10 my-4'>
              <Typography.Text type={isValid('recovery_email') ? 'danger' : 'secondary'} className={`mb-1 block ${isValid('recovery_email') ? 'text-(--uizz-antd-color-error)' : ''}`}>
                Nome do remetente de e-mail para recuperação de vendas
                <Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text >
              </Typography.Text>
              <Input
                id='recovery_email'
                status={isValid('recovery_email') ? 'error' : undefined}
                value={product.recovery_email}
                onChange={(e: any) => {
                  if (handleChange) handleChange('recovery_email', e.target.value);
                }}
                disabled={!product.sales_recovery}
              />
              <Typography.Text
                className={`mt-1 block ${isValid('recovery_email') ? 'text-(--uizz-antd-color-error)' : 'text-content-subtitle'}`}
              >
                {isValid('recovery_email') ? errorFor('recovery_email') : 'Ex: Fulano da Silva - Eduzz'}
              </Typography.Text>
            </div>
          </Tooltip>
        )}
      </div>

      <Modal
        open={showAlertUncheck}
        onCancel={handleToggleAlertVisibility}
        title='Tem certeza que deseja desativar esta funcionalidade?'
        footer={
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button id='new-product-sales-recovery-disable' type='text' onClick={() => handleUncheckConfirmed(product)}>
              Desativar mesmo assim
            </Button>
            <Button id='new-product-sales-recovery-keep' type='primary' onClick={handleToggleAlertVisibility}>
              Manter ativado
            </Button>
          </div>
        }
      >
        <Typography.Paragraph>
          Mantenha a RVP ativa para recuperar até 10% das suas vendas e aumentar suas conversões com clientes que
          abandonam o carrinho de forma automática.
        </Typography.Paragraph>
      </Modal>
    </FormContent>
  );
}

export default SalesRecovery;
